import { gsap } from "gsap"
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import type { NextPage } from "next"
import Head from "next/head"
import Image, { type StaticImageData } from "next/image"
import { type FC, useEffect, useRef, useState } from "react"

import Header from "../components/Header"
import UbiqButton, { UbiqButtonSize, UbiqButtonType } from "../components/UbiqButton"
import block1_1 from "../public/assets/images/landing/block-1-1.png"
import block1_2 from "../public/assets/images/landing/block-1-2.png"
import block1_3 from "../public/assets/images/landing/block-1-3.png"
import block1_4 from "../public/assets/images/landing/block-1-4.png"
import block2_1 from "../public/assets/images/landing/block-2-1.png"
import block2_2 from "../public/assets/images/landing/block-2-2.png"
import block2_3 from "../public/assets/images/landing/block-2-3.png"
import block2_4 from "../public/assets/images/landing/block-2-4.png"
import block3_1 from "../public/assets/images/landing/block-3-1.png"
import block3_2 from "../public/assets/images/landing/block-3-2.png"
import block3_3 from "../public/assets/images/landing/block-3-3.png"
import block3_4 from "../public/assets/images/landing/block-3-4.png"
import block4_1 from "../public/assets/images/landing/block-4-1.png"
import block4_2 from "../public/assets/images/landing/block-4-2.png"
import block4_3 from "../public/assets/images/landing/block-4-3.png"
import block4_4 from "../public/assets/images/landing/block-4-4.png"
import cloud1 from "../public/assets/images/landing/cloud-1.png"
import cloud2 from "../public/assets/images/landing/cloud-2.png"
import cloud3 from "../public/assets/images/landing/cloud-3.png"
import { useWindowSize } from "../utils/useWindowSize"

const SECTIONS = ["section-1", "section-2", "section-3", "section-4", "section-5", "section-6"]

const WHY_TEXT = [
  "Dear community creators,",
  "For some, 'the good life' means wealth and success, while for others, it's peace, relationships, health, societal contribution, or just having fun. For us, it's all true, distilled into balanced simplicity and meaningful connections. At WERZ, we go beyond being a platform; we're your committed community partner. Our success lies in cultivating spaces where communities evolve alongside members, making sure they strive — We got you!",
  "Yours,",
  "Werzy",
]

const DEMOS = [
  {
    copy: "Customize the design in detail with our drag-and-drop editor",
    poster: "/assets/images/landing/posters/drag-and-drop.png",
    src: "/assets/videos/drag-and-drop.mp4",
  },
  {
    copy: "Choose the modules with features that cover all your needs",
    poster: "/assets/images/landing/posters/modules.png",
    src: "/assets/videos/modules.mp4",
  },
  {
    copy: "Build on unique insights and data from our model",
    poster: "/assets/images/landing/posters/our-model.png",
    src: "/assets/videos/our-model.mp4",
  },
]

const WHEEL_LINE_HEIGHT_IN_PX = 16
const SCROLL_OVERFLOW_VALUES = new Set(["auto", "scroll", "overlay"])

const toPixelDelta = (delta: number, deltaMode: number) => {
  if (deltaMode === 1) return delta * WHEEL_LINE_HEIGHT_IN_PX
  if (deltaMode === 2) return delta * window.innerHeight
  return delta
}

const canScrollElement = (element: HTMLElement, deltaY: number) => {
  const { overflowY } = window.getComputedStyle(element)
  const supportsVerticalScroll =
    SCROLL_OVERFLOW_VALUES.has(overflowY) && element.scrollHeight > element.clientHeight

  if (!supportsVerticalScroll) return false

  if (deltaY < 0) return element.scrollTop > 0
  if (deltaY > 0) return element.scrollTop + element.clientHeight < element.scrollHeight - 1

  return false
}

const hasScrollableAncestor = (
  startNode: HTMLElement,
  boundaryNode: HTMLElement,
  deltaY: number,
) => {
  let node: HTMLElement | null = startNode

  while (node && node !== boundaryNode && node !== document.body) {
    if (canScrollElement(node, deltaY)) return true
    node = node.parentElement
  }

  return false
}

const canScrollViewport = (deltaY: number) => {
  const root = document.documentElement
  const body = document.body
  const scrollTop = window.scrollY || root.scrollTop || body.scrollTop
  const scrollHeight = Math.max(root.scrollHeight, body.scrollHeight)
  const maxScrollTop = scrollHeight - window.innerHeight

  if (maxScrollTop <= 0) return false
  if (deltaY < 0) return scrollTop > 0
  if (deltaY > 0) return scrollTop < maxScrollTop - 1

  return false
}

const getTargetElement = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return null

  let node: Element | null = target
  while (node && !(node instanceof HTMLElement)) {
    node = node.parentElement
  }

  return node
}

const BLOCK_BADGES = {
  commerce: {
    bgColor: "bg-[#E7A627]",
    name: "commerce",
  },
  design: {
    bgColor: "bg-[#5900D9]",
    name: "design",
  },
  insights: {
    bgColor: "bg-[#1FBBC5]",
    name: "insights",
  },
  marketingTools: {
    bgColor: "bg-[#FF1C5E]",
    name: "marketing tools",
  },
  modules: {
    bgColor: "bg-[#B78AF7]",
    name: "modules",
  },
  network: {
    bgColor: "bg-[#2C883D]",
    name: "network",
  },
}

const BLOCKS_INFO = [
  {
    badges: [BLOCK_BADGES.commerce, BLOCK_BADGES.modules],
    description:
      "Whether it's merch, event tickets, digital or any other products, they can all be sold directly within your community.",
    id: "block-1-1",
    src: block1_1,
    title: "Open your shop",
  },
  {
    badges: [BLOCK_BADGES.network],
    description:
      "Stand alongside like-minded communities, harnessing the collective power of network effects together. Great for you and your members.",
    id: "block-1-2",
    src: block1_2,
    title: "Be among top communities",
  },
  {
    badges: [BLOCK_BADGES.design],
    description:
      "Our designers have made unique templates. Pick the one that suits your community, make the changes you need and to get started.",
    id: "block-1-3",
    src: block1_3,
    title: "Use our templates",
  },
  {
    badges: [BLOCK_BADGES.insights],
    description:
      "Give special access to modules, features, events, and other things to some members earlier than others.",
    id: "block-1-4",
    src: block1_4,
    title: "Segment your community",
  },
  {
    badges: [BLOCK_BADGES.modules],
    description:
      "Streamline event management: sell tickets, invite members, and keep everyone informed about activities all in one place.",
    id: "block-2-1",
    src: block2_1,
    title: "Run events",
  },
  {
    badges: [BLOCK_BADGES.modules],
    description: "Extend your community world by curating your own radio.",
    id: "block-2-2",
    src: block2_2,
    title: "Broadcast your own radio",
  },
  {
    badges: [BLOCK_BADGES.marketingTools],
    description:
      "Choose whether you want your community to be private or public, and create unique invite codes to invite whomever you want.",
    id: "block-2-3",
    src: block2_3,
    title: "Private or public?",
  },
  {
    badges: [BLOCK_BADGES.insights, BLOCK_BADGES.marketingTools],
    description:
      "Easily onboard and move members from other platforms with our straightforward process.",
    id: "block-2-4",
    src: block2_4,
    title: "Smoothly onboard members",
  },
  {
    badges: [BLOCK_BADGES.insights],
    description:
      "Members, purchases, views, check-ins and more – our tools offer valuable insights and recommendations based from 1000 data points.",
    id: "block-3-1",
    src: block3_1,
    title: "All data and analytics in one place",
  },
  {
    badges: [BLOCK_BADGES.insights],
    description:
      "No more solo spreadsheet sessions. We give you the necessary data and draw conclusions for you.",
    id: "block-3-2",
    src: block3_2,
    title: "We are your data scientists",
  },
  {
    badges: [BLOCK_BADGES.commerce],
    description:
      "We've connected with a bunch of payment providers and media providers, and a lot more. Most of it is just one click away.",
    id: "block-3-3",
    src: block3_3,
    title: "Add 3rd party integrations",
  },
  {
    badges: [BLOCK_BADGES.marketingTools],
    description:
      "Show love and appreciation towards members by using extensive promo code features.",
    id: "block-3-4",
    src: block3_4,
    title: "Create promo codes",
  },
  {
    badges: [BLOCK_BADGES.modules],
    description:
      "Facilitate seamless member interaction by creating forums centered around various topics and news.",
    id: "block-4-1",
    src: block4_1,
    title: "Add forum for more engagement",
  },
  {
    badges: [BLOCK_BADGES.marketingTools],
    description:
      "Use top converting tools like crafting custom push notifications to stay close with your members at all times.",
    id: "block-4-2",
    src: block4_2,
    title: "Start using custom notification",
  },
  {
    badges: [BLOCK_BADGES.marketingTools],
    description:
      "Use our email marketing tool to convert and get the insights in one place. Or simply get your message to your members via our built-in chat.",
    id: "block-4-3",
    src: block4_3,
    title: "Target with built-in email and chat",
  },
  {
    badges: [BLOCK_BADGES.insights, BLOCK_BADGES.marketingTools],
    description:
      "Use our step-by-step guides to elevate your community to the next level and turn it into a business.",
    id: "block-4-4",
    src: block4_4,
    title: "Follow the blueprints",
  },
]

const newOrder = [0, 4, 8, 12, 1, 5, 9, 13, 2, 6, 10, 14, 3, 7, 11, 15]
const REORDERED_BLOCKS_INFO = newOrder.map((i) => BLOCKS_INFO[i])

const TypewriterText: FC<{
  part1: string
  part2: string
  part3: string
  part4: string
  scrollProgress: number
}> = ({ scrollProgress, part1, part2, part3, part4 }) => {
  const { isMiniMobileScreen } = useWindowSize()

  const normalTextClassName = `md:text-[24px] ${
    isMiniMobileScreen ? "text-[0.8rem] leading-[1.9rem]" : "text-[18px]"
  } font-mono font-medium`
  const redTextClassName = "md:text-[40px] text-[30px] text-[red] font-sans leading-10"

  // Calculate the total length of all parts
  const totalLength = part1.length + part2.length + part3.length

  // Determine how much text should be displayed in total based on scrollProgress
  const totalDisplayLength = Math.floor(scrollProgress * totalLength)

  // Determine how much of each part to display
  const displayedPart1 = part1.substring(0, Math.min(totalDisplayLength, part1.length))
  const remainingLengthForPart2 = Math.max(0, totalDisplayLength - part1.length)
  const displayedPart2 = part2.substring(0, Math.min(remainingLengthForPart2, part2.length))
  const remainingLengthForPart3 = Math.max(0, totalDisplayLength - part1.length - part2.length)
  const displayedPart3 = part3.substring(0, Math.min(remainingLengthForPart3, part3.length))
  const remainingLengthForPart4 = Math.max(
    0,
    totalDisplayLength - part2.length - (part3.length + part1.length),
  )
  const displayedPart4 = part4.substring(0, Math.min(remainingLengthForPart4, part4.length))

  return (
    <div className="relative md:w-[800px] md:h-[62vh] h-[78vh] w-[95vw] md:mb-8 md:mt-[0vh] mt-[0px] flex flex-col gap-5 items-start justify-center cursor-default">
      <div className="relative">
        {/* Adjust height as needed */}
        <span className={`text-[#A4A5A7] ${normalTextClassName}`}>{part1}</span>
        <span className={`text-[#000] ${normalTextClassName} absolute top-0 left-0`}>
          {displayedPart1}
        </span>
      </div>
      <div className="relative">
        {/* Adjust height as needed */}
        <span className={`text-[#A4A5A7] ${normalTextClassName}`}>{part2}</span>
        <span className={`text-[#000] ${normalTextClassName} absolute top-0 left-0`}>
          {displayedPart2}
        </span>
      </div>
      <div className="relative">
        <span className={`opacity-0 ${normalTextClassName}`}>{part3}</span>
        <span className={`${normalTextClassName} absolute top-0 left-0`}>{displayedPart3}</span>
      </div>
      <div className="relative">
        <span className={`opacity-0 ${redTextClassName}`}>{part4}</span>
        <span className={`font-normal ${redTextClassName} absolute top-0 left-0`}>
          {displayedPart4}
        </span>
      </div>
    </div>
  )
}

const Block: FC<{
  id: string
  title: string
  description: string
  src: StaticImageData
  badges: { name: string; bgColor: string }[]
}> = ({ id, badges, src, title, description }) => (
  <div
    className="md:w-[268px] w-[90vw] rounded-[20px] bg-[#fff] cursor-default border-[1px] border-black"
    id={id}
  >
    <Image alt="" className="w-full rounded-tl-[20px] rounded-tr-[20px]" src={src} />
    <div className="p-4 flex flex-col gap-2">
      <p className="text-[14px] font-mono font-medium leading-[17px]">{title}</p>
      <p className="text-[12px] font-mono font-light leading-4 text-[#1B1D22]">{description}</p>
      <div className="flex gap-[8px]">
        {badges.map((b, key) => (
          <p
            className={`rounded-full h-[23px] text-[12px] leading-[12px] text-[#fff] flex items-center font-mono px-[9px] pb-[2px] self-start ${b.bgColor}`}
            key={key}
          >
            {b.name}
          </p>
        ))}
      </div>
    </div>
  </div>
)

const configureBackgroundAnimations = (isDesktop: boolean) => {
  gsap.to("#section-1", {
    opacity: 0,
    scrollTrigger: {
      end: "110% center",
      onUpdate: (self) => gsap.to("#section-1", { zIndex: self.progress > 0.98 ? -20 : 30 }),
      scrub: true,
      start: "55% center",
      trigger: "#section-1",
    },
  })

  gsap.to("#section-2-background", {
    opacity: 1,
    scrollTrigger: {
      end: "-10% center",
      scrub: true,
      start: "-20% center",
      trigger: "#section-3",
    },
  })

  gsap.to("#section-4-background", {
    opacity: 1,
    scrollTrigger: {
      end: "0% center",
      scrub: true,
      start: "-10% center",
      trigger: "#section-4",
    },
  })

  gsap.to("#section-5-background", {
    opacity: 1,
    scrollTrigger: {
      end: "-10% center",
      scrub: true,
      start: "-30% center",
      trigger: "#section-5",
    },
  })

  gsap.to("#section-6-background", {
    opacity: 1,
    scrollTrigger: {
      end: "15% center",
      scrub: true,
      start: isDesktop ? "-16% center" : "-2% center",
      trigger: "#section-6",
    },
  })

  gsap.to("#section-7-background", {
    opacity: 1,
    scrollTrigger: {
      end: isDesktop ? "120% center" : "115% center",
      onEnter: () => {
        // console.log("Section 7 background animation triggered - entering")
        document.getElementById("section-7-background")?.style.setProperty("visibility", "visible")
      },
      onLeaveBack: () => {
        // console.log("Section 7 background animation triggered - leaving back")
        document.getElementById("section-7-background")?.style.setProperty("visibility", "hidden")
        document.getElementById("section-7-background")?.style.setProperty("opacity", "0")
      },
      scrub: true,
      start: "110% center",
      trigger: "#section-6",
    },
    visibility: "visible",
    y: -100,
  })
}

const configureSection2Animations = (isDesktop: boolean) => {
  const typewriterTimeline = gsap.timeline({
    scrollTrigger: {
      end: "80% center",
      scrub: true,
      start: isDesktop ? "8.8% center" : "11% center",
      trigger: "#section-2",
    },
  })

  typewriterTimeline
    .to("#typewriter", { display: "flex", duration: 1, opacity: 1 }) // First fade in and display
    .to("#typewriter", { duration: 1, opacity: 0 }, "+=1") // Then fade out after a delay
}

const configureSection3Animations = (isDesktop: boolean) => {
  const phonePreviewTimeline = gsap.timeline({
    scrollTrigger: {
      end: "bottom center",
      scrub: true,
      start: "top center",
      trigger: "#section-3",
    },
  })

  phonePreviewTimeline
    .to("#phone-frame", { ease: "none", opacity: 1 }, "0%")
    // Stay fully visible for 60% of the section (20% to 80%)
    .to("#phone-frame", { ease: "none", opacity: 1 }, "20%")
    // Fade out over the last 20% of the section (80% to 100%)
    .to("#phone-frame", { ease: "power4.out", opacity: 0 }, "80%")

  gsap.to("#section-3-cloud-1", {
    scrollTrigger: {
      end: "120% center",
      onUpdate: (self) => {
        const progress = self.progress
        let opacity = 1
        if (progress > 0.7) opacity = 1 - progress * 0.2
        gsap.to("#section-3-cloud-1", { opacity: opacity })
      },
      scrub: true,
      start: "top center",
      trigger: "#section-3",
    },
    x: isDesktop ? 0 : 100,
    y: -1000,
  })

  gsap.to("#section-3-cloud-2", {
    scrollTrigger: {
      end: "120% center",
      onUpdate: (self) => {
        const progress = self.progress
        let opacity = 1
        if (progress > 0.7) opacity = 1 - progress * 0.2
        gsap.to("#section-3-cloud-2", { opacity: opacity })
      },
      scrub: true,
      start: "top center",
      trigger: "#section-3",
    },
    x: isDesktop ? 0 : -100,
    y: -1000,
  })

  gsap.to("#section-3-cloud-3", {
    scrollTrigger: {
      end: "120% center",
      onUpdate: (self) => {
        const progress = self.progress
        let opacity = 1
        if (progress > 0.7) opacity = 1 - progress * 0.2
        gsap.to("#section-3-cloud-3", { opacity: opacity })
      },
      scrub: true,
      start: "top center",
      trigger: "#section-3",
    },
    x: isDesktop ? 0 : 310,
    y: -window.innerHeight * 1.9,
  })

  gsap.to("#section3-main-text-row-1", {
    display: "flex",
    opacity: 1,
    scrollTrigger: {
      end: "15% center",
      scrub: true,
      start: "10% center",
      trigger: "#section-3",
    },
    y: -120,
  })

  gsap.to("#section3-main-text-row-2", {
    display: "flex",
    opacity: 1,

    scrollTrigger: {
      end: "20% center",
      scrub: true,
      start: "15% center",
      trigger: "#section-3",
    },
    y: -120,
  })

  gsap.to("#section3-main-text-row-3", {
    display: "flex",
    opacity: 1,
    scrollTrigger: {
      end: "25% center",
      scrub: true,
      start: "20% center",
      trigger: "#section-3",
    },
    y: -120,
  })

  gsap.to("#section3-main-text-row-4", {
    display: "flex",
    opacity: 1,
    scrollTrigger: {
      end: "30% center",
      scrub: true,
      start: "25% center",
      trigger: "#section-3",
    },
  })

  gsap.to("#section3-main-text-row-5", {
    display: "flex",
    opacity: 1,
    scrollTrigger: {
      end: "35% center",
      scrub: true,
      start: "30% center",
      trigger: "#section-3",
    },
  })

  gsap.to("#section3-main-text-row-6", {
    display: "flex",
    opacity: 1,
    scrollTrigger: {
      end: "40% center",
      scrub: true,
      start: "35% center",
      trigger: "#section-3",
    },
  })

  gsap.to("#section3-main-text-row-7", {
    display: "flex",
    opacity: 1,
    scrollTrigger: {
      end: "45% center",
      scrub: true,
      start: "40% center",
      trigger: "#section-3",
    },
  })

  const section3rowIds = [
    "#section3-main-text-row-1",
    "#section3-main-text-row-2",
    "#section3-main-text-row-3",
    "#section3-main-text-row-4",
    "#section3-main-text-row-5",
    "#section3-main-text-row-6",
    "#section3-main-text-row-7",
  ]

  section3rowIds.forEach((id) => {
    gsap.to(id, {
      scrollTrigger: {
        end: "100% center",
        scrub: true,
        start: "80% center",
        trigger: "#section-3",
      },
      x: isDesktop ? 0 : window.innerWidth,
    })
  })
}

const configureSection4Animations = (setDemo: (x: any) => void) => {
  const demoData = [
    { demoIndex: 0, end: 0.36999, start: 0.26, target: "#section-4-text-row-1" },
    { demoIndex: 1, end: 0.51999, start: 0.37, target: "#section-4-text-row-2" },
    { demoIndex: 2, end: 1, start: 0.52, target: "#section-4-text-row-3" },
  ]

  gsap.to("#section-4-preview", {
    scrollTrigger: {
      end: "20% center",
      scrub: true,
      start: "-10% center",
      trigger: "#section-4",
    },
    y: window.innerHeight * 1.25,
  })

  gsap.to("#section-4-preview", {
    scrollTrigger: {
      end: "100% center",
      onUpdate: (self) => {
        const progress = self.progress
        demoData.forEach(({ start, end, target, demoIndex }) => {
          const color = progress > start && progress < end ? "#1B1D22" : "#D2D2D2"
          gsap.to(target, { color, duration: 0.1 })
          if (color === "#1B1D22") setDemo(DEMOS[demoIndex])
        })
      },
      scrub: true,
      start: "0% center",
      trigger: "#section-4",
    },
  })

  gsap.to("#section-4-preview", {
    ease: "power2.in",
    opacity: 0,
    scrollTrigger: {
      end: "100% center",
      scrub: true,
      start: "80% center",
      trigger: "#section-4",
    },
  })

  gsap.to("#section-4-text", {
    scrollTrigger: {
      end: "100% center",
      scrub: true,
      start: "0% center",
      trigger: "#section-4",
    },
    y: -window.innerHeight * 0.9,
  })

  gsap.to("#section-4-text", {
    opacity: 0,
    scrollTrigger: {
      end: "90% center",
      scrub: true,
      start: "80% center",
      trigger: "#section-4",
    },
  })
}

const configureSection5Animations = () => {
  gsap.to("#section5-main-text-row1", {
    scrollTrigger: {
      end: "300% top",
      onUpdate: (self) => {
        const progress = self.progress
        let opacity = 1
        if (progress > 0.2) opacity = 1 - progress * 2
        gsap.to("#section5-main-text-row1", { opacity: opacity })
        gsap.to("#section5-main-text-row2", { opacity: opacity })
        gsap.to("#section5-main-text-subtitle", { opacity: opacity })
      },
      pin: true,
      scrub: true,
      start: "80% center",
      trigger: "#section-5",
    },
  })

  const section5WordsForRow1 = ["word1", "word2", "word3", "word4"]
  const section5WordsForRow2 = ["word1", "word2", "word3", "word4", "word5", "word6"]

  section5WordsForRow1.forEach((word, index) => {
    gsap.to(`#section5-main-text-row1-${word}`, {
      opacity: 1,
      scrollTrigger: {
        end: `-${350 - index * 30}% center`,
        scrub: true,
        start: `-${380 - index * 30}% center`,
        trigger: "#section5-main-text-row1",
      },
      y: -50,
    })
  })

  section5WordsForRow2.forEach((word, index) => {
    gsap.to(`#section5-main-text-row2-${word}`, {
      opacity: 1,
      scrollTrigger: {
        end: `-${220 - index * 30}% center`,
        scrub: true,
        start: `-${250 - index * 30}% center`,
        trigger: "#section5-main-text-row1",
      },
      y: -50,
    })
  })

  const rowsData = [
    { end: "10%", id: 3, start: "-70%" },
    { end: "60%", id: 4, start: "-10%" },
    { end: "110%", id: 5, start: "50%" },
    { end: "160%", id: 6, start: "110%" },
  ]

  rowsData.forEach(({ id, start, end }) => {
    gsap.to(`#section5-main-text-row${id}`, {
      opacity: 1,
      scrollTrigger: {
        end: `${end} center`,
        scrub: true,
        start: `${start} center`,
        trigger: "#section5-main-text-row1",
      },
    })
  })
}

const configureSection6Animations = (isDesktop: boolean) => {
  const section6WordsForRow1 = ["word1", "word2", "word3", "word4", "word5"]

  const additionalValue = isDesktop ? -18 : 0

  section6WordsForRow1.forEach((word, index) => {
    gsap.to(`#section6-main-text-row1-${word}`, {
      opacity: 1,
      scrollTrigger: {
        end: `${additionalValue + 2 + index * 2}% center`,
        scrub: true,
        start: `${additionalValue + index * 2}% center`,
        trigger: "#section-6",
      },
      y: "-50",
    })
  })

  const section6WordsForRow2 = ["word1", "word2", "word3", "word4", "word5"]

  section6WordsForRow2.forEach((word, index) => {
    gsap.to(`#section6-main-text-row2-${word}`, {
      opacity: 1,
      scrollTrigger: {
        end: `${additionalValue + 12 + index * 2}% center`,
        scrub: true,
        start: `${additionalValue + 10 + index * 2}% center`,
        trigger: "#section-6",
      },
      y: "-50",
    })
  })

  const section6WordsForRow3 = ["word1", "word2", "word3"]

  section6WordsForRow3.forEach((word, index) => {
    gsap.to(`#section6-main-text-row3-${word}`, {
      opacity: 1,
      scrollTrigger: {
        end: `${additionalValue + 22 + index * 2}% center`,
        scrub: true,
        start: `${additionalValue + 20 + index * 2}% center`,
        trigger: "#section-6",
      },
      y: "-50",
    })
  })

  gsap.to("#section-6-cloud", {
    scrollTrigger: {
      end: "90% center",
      scrub: true,
      start: "-40% center",
      trigger: "#section-6",
    },
    y: -window.innerHeight * 1.8,
  })

  gsap.to("#section6-button", {
    opacity: 1,
    scrollTrigger: {
      end: "27% center",
      scrub: true,
      start: "12% center",
      trigger: "#section6-main-text2",
    },
  })

  gsap.to("#section6-main-text2", {
    scrollTrigger: {
      end: "145% center",
      pin: true,
      scrub: true,
      start: "78% center",
      trigger: "#section-6",
    },
    y: -400,
  })

  const symbolsData = [
    { end: -18, id: 1, start: -21 },
    { end: -15, id: 2, start: -18 },
    { end: -12, id: 3, start: -15 },
    { end: -9, id: 4, start: -12 },
    { end: -6, id: 5, start: -9 },
    { end: -3, id: 6, start: -6 },
    { end: 0, id: 7, start: -3 },
    { end: 3, id: 8, start: 0 },
    { end: 6, id: 9, start: 3 },
    { end: 9, id: 10, start: 6 },
    { end: 12, id: 11, start: 9 },
    { end: 15, id: 12, start: 12 },
  ]

  symbolsData.forEach(({ id, start, end }) => {
    gsap.to(`#section6-main-text-symbol-${id}`, {
      opacity: 1,
      scrollTrigger: {
        end: `${end}% center`,
        scrub: true,
        start: `${start}% center`,
        trigger: "#section6-main-text2",
      },
      y: -40,
    })
  })
}

const Landing: NextPage = () => {
  const [secondSection, setSecondSection] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [firstSection, setFirstSection] = useState(true)
  const [isLoadMore, setIsLoadMore] = useState(false)
  const [demo, setDemo] = useState(DEMOS[0])
  const secondSectionRef = useRef(false)

  const { isDesktop, isMiniMobileScreen, height } = useWindowSize()

  // Ensure section-7-background is hidden on initial load
  useEffect(() => {
    // Force section-7-background to be hidden on initial load
    const section7Background = document.getElementById("section-7-background")
    if (section7Background) {
      section7Background.style.setProperty("opacity", "0", "important")
      section7Background.style.setProperty("visibility", "hidden", "important")

      // Check if we're on iOS Safari
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

      if (isIOS && isSafari) {
        // console.log("iOS Safari detected - applying special handling for section-7-background")
      }
    }
  }, [])

  useEffect(() => {
    // Ensure scroll is at top before initializing ScrollTrigger (fixes in-app browser issues)
    window.scrollTo(0, 0)

    gsap.registerPlugin(ScrollToPlugin)
    gsap.registerPlugin(ScrollTrigger)

    const searchParams = new URLSearchParams(window.location.search)
    const normalizeOverride = searchParams.get("wmLandingNormalize")
    const shouldNormalize = normalizeOverride === "1" || (normalizeOverride !== "0" && !isDesktop)

    // Normalize scroll to fix macOS Chrome momentum scrolling issues with pinned elements
    if (shouldNormalize) {
      ScrollTrigger.normalizeScroll(true)
    }

    const onWheel = (event: WheelEvent) => {
      const target = getTargetElement(event.target)
      if (!target) return
      if (event.ctrlKey) return

      const pixelDeltaX = toPixelDelta(event.deltaX, event.deltaMode)
      const pixelDeltaY = toPixelDelta(event.deltaY, event.deltaMode)

      // Preserve native horizontal trackpad gestures (browser back/forward).
      if (Math.abs(pixelDeltaX) >= Math.abs(pixelDeltaY)) return
      if (!pixelDeltaY) return

      const hasScrollable = hasScrollableAncestor(target, document.body, pixelDeltaY)
      const viewportCanScroll = canScrollViewport(pixelDeltaY)
      if (hasScrollable || !viewportCanScroll) return

      if (event.cancelable) event.preventDefault()
      window.scrollBy({ top: pixelDeltaY, left: 0, behavior: "auto" })
    }

    // Desktop fallback: keep native swipe gestures (no normalize) and prevent random wheel freeze.
    if (isDesktop && !shouldNormalize) {
      document.addEventListener("wheel", onWheel, { capture: true, passive: false })
    }

    // bottom arrow
    gsap.to("#arrow", {
      rotate: 180,
      scrollTrigger: {
        end: "112% center",
        scrub: true,
        start: "110% center",
        trigger: "#landing",
      },
    })

    // sections configuration
    // this is done for switching section names
    gsap.timeline({
      scrollTrigger: {
        end: "bottom center",
        onUpdate: (self) => {
          const progress = self.progress
          if (progress >= 0 && progress <= 0.0095) setFirstSection(true)
          else setFirstSection(false)
          if (progress > 0.0095 && progress <= 0.287) {
            secondSectionRef.current = true
            setSecondSection(true)
          } else {
            secondSectionRef.current = false
          }
        },
        scrub: true,
        start: "top center",
        trigger: "#landing",
      },
    })

    // scrollProgress for section2
    gsap.timeline({
      scrollTrigger: {
        end: "bottom center",
        onUpdate: (self) => {
          if (secondSectionRef.current) setScrollProgress(self.progress * 2.5)
        },
        scrub: true,
        start: "top center",
        trigger: `#section-2`,
      },
    })

    configureBackgroundAnimations(isDesktop)

    configureSection2Animations(isDesktop)
    configureSection3Animations(isDesktop)
    configureSection4Animations(setDemo)
    configureSection5Animations()
    configureSection6Animations(isDesktop)

    // Refresh ScrollTrigger to ensure correct initial state (fixes in-app browser issues)
    ScrollTrigger.refresh()

    // Ensure first section state is correct when at top of page
    if (window.scrollY === 0) {
      setFirstSection(true)
    }

    return () => {
      if (shouldNormalize) {
        ScrollTrigger.normalizeScroll(false)
      }
      if (isDesktop && !shouldNormalize) {
        document.removeEventListener("wheel", onWheel, { capture: true })
      }
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
    }
  }, [isDesktop])

  const sendEmail = () => window.open("/form", "_blank", "noopener,noreferrer")

  const thumbnail = "https://media.werz.at/web/werz-thumbnail"

  return (
    <div className="relative" id="landing">
      <Head>
        <meta content="website" property="og:type" />
        <meta content="https://werz.at" property="og:url" />
        <meta content={thumbnail} property="og:image" />
        <meta content={thumbnail} property="og:image:secure_url" />
        <meta content={thumbnail} name="twitter:image" />
        <meta
          content="Bringing balanced simplicity and meaningful connections to subcultures around the world."
          property="twitter:description"
        />
        <meta content="summary_large_image" name="twitter:card" />
        <meta content="WERZ – the good life." property="og:title" />
        <meta
          content="Bringing balanced simplicity and meaningful connections to subcultures around the world."
          property="og:description"
        />
        <title>WERZ</title>
        <link href="/favicon.ico" rel="icon" />
        <meta content="#c3d4ec" media="(prefers-color-scheme: light)" name="theme-color" />
        <meta content="#c3d4ec" media="(prefers-color-scheme: dark)" name="theme-color" />
      </Head>
      <Header
        className="fixed flex items-center justify-between left-7 right-6 z-40 mt-6 top-0"
        isButton={!firstSection}
        logoColor={firstSection ? "#FFF" : "#1B1D22"}
        onClick={sendEmail}
      />
      {/*Section 1*/}
      <div
        className="fixed z-30 left-0 right-0 top-0 md:h-[100vh] h-[calc(100vh+110px)] bg-[#c3d4ec]"
        id={SECTIONS[0]}
      >
        <video
          autoPlay
          className="hidden md:block w-full h-full absolute object-cover translate-x-[10.5vw] translate-y-[-5vh] scale-125"
          controls={false}
          loop
          muted
          playsInline
          poster="/assets/images/landing/posters/planet-walk-desktop.png"
          preload="metadata"
          src="/assets/videos/planet-walk-desktop-optimised.mp4"
        />
        <video
          autoPlay
          className="block md:hidden w-full h-full absolute object-cover translate-y-[-6vh]"
          controls={false}
          loop
          muted
          playsInline
          poster="/assets/images/landing/posters/planet-walk-mobile.png"
          preload="metadata"
          src="/assets/videos/planet-walk-mobile-optimised.mp4"
        />
        <div className="flex flex-col md:gap-6 gap-3 items-start md:justify-center h-[100dvh] md:pt-[0vh] pt-[13vh] relative">
          <p className="md:mx-0 mx-[6vw] md:ml-[8vw] md:w-[650px] md:text-[60px] text-[30px] text-[#fff] font-medium font-sans md:leading-[60px] leading-[30px] cursor-default">
            Communities, it's time to take the next step.
          </p>
          <p className="md:mx-0 mx-[6vw] md:ml-[8vw] md:w-[522px] md:text-[16px] text-[14px] text-[#fff] font-mono md:leading-5 leading-[120%] cursor-default">
            In the realm of tech, culture, sports, or meatspace, WERZ gets you. We see every
            community creator as a visionary for the future. What we do? Offer the ideal platform
            and tools for you to craft, build, and take your community to the next level.
          </p>
          <div className="md:sticky md:flex md:justify-start absolute bottom-[9vh] md:ml-[8vw] w-full z-30">
            <UbiqButton
              buttonType={UbiqButtonType.LIGHT}
              onClick={() => void sendEmail()}
              size={UbiqButtonSize.SMALL}
              styles="uppercase font-mono"
              title="JOIN WERZ"
            />
          </div>
          <div className="md:text-[13px] w-full uppercase text-[#FFFFFF] flex justify-center absolute md:bottom-[6vh] bottom-[3vh] font-mono cursor-default">
            Scroll to Explore
          </div>
        </div>
      </div>
      {/*Section 2*/}
      <div className="flex justify-center h-[330vh] relative bg-white mt-[107vh]" id={SECTIONS[1]}>
        <div
          className="md:px-0 px-[6%] fixed z-10 top-0 left-0 w-screen items-center justify-center h-screen opacity-0 hidden"
          id="typewriter"
          // this is done to circumvent the issue with in-app browsers on iOS and resizing bottom bar
          style={{ height: height + 70 }}
        >
          <TypewriterText
            part1={WHY_TEXT[0]}
            part2={WHY_TEXT[1]}
            part3={WHY_TEXT[2]}
            part4={WHY_TEXT[3]}
            scrollProgress={scrollProgress}
          />
        </div>
      </div>
      {/*Section 3*/}
      <div
        className="flex md:flex-row flex-col-reverse items-center md:gap-0 gap-12 h-[100vh] md:px-[6%] relative"
        id={SECTIONS[2]}
      >
        <Image
          alt=""
          className="md:w-[19vw] absolute md:right-[30vw] right-[-2vw] md:bottom-[15vh] bottom-[-2vh] z-20"
          id="section-3-cloud-1"
          src={cloud1}
        />
        <Image
          alt=""
          className="md:w-[23vw] absolute md:left-[-6vw] left-[-15vw] md:bottom-[-15vh] bottom-[-25vh] z-20"
          id="section-3-cloud-2"
          src={cloud2}
        />
        <Image
          alt=""
          className="md:w-[25vw] absolute md:left-[35vw] left-[-13vw] bottom-[-144vh] md:z-40 z-20"
          id="section-3-cloud-3"
          src={cloud3}
        />
        <div className="md:ml-[11vw] flex flex-col md:mb-[-150px] md:items-start md:gap-7 gap-2 md:px-0 px-[6%] relative z-20 cursor-default">
          {isDesktop ? (
            <div className="md:w-[570px]">
              <p
                className="md:sticky fixed left-[5vw] bottom-[6vh] md:text-[50px] text-[25px] font-sans md:leading-[45px] leading-[27px] opacity-0"
                id="section3-main-text-row-1"
              >
                Your community is
              </p>
              <p
                className="md:sticky fixed left-[5vw] bottom-[3vh] md:text-[50px] text-[25px] font-sans md:leading-[45px] leading-[27px] opacity-0"
                id="section3-main-text-row-2"
              >
                unique, so should your
              </p>
              <p
                className="md:sticky fixed left-[5vw] bottom-[3vh] md:text-[50px] text-[25px] font-sans md:leading-[45px] leading-[27px] opacity-0"
                id="section3-main-text-row-3"
              >
                digital space be.
              </p>
            </div>
          ) : (
            <div>
              <p
                className="md:sticky fixed left-[5vw] bottom-[6vh] md:text-[50px] text-[25px] font-sans md:leading-[45px] leading-[27px]"
                id="section3-main-text-row-1"
                style={{ opacity: 0 }}
              >
                Your community is unique, so
              </p>
              <p
                className="md:sticky fixed left-[5vw] bottom-[3vh] md:text-[50px] text-[25px] font-sans md:leading-[45px] leading-[27px]"
                id="section3-main-text-row-2"
                style={{ opacity: 0 }}
              >
                should your digital space be.
              </p>
            </div>
          )}
          <div className="md:sticky fixed left-[5vw] bottom-[13vh] md:w-[420px] md:mt-[-120px] z-10">
            <p
              className={`md:text-[16px] ${
                isMiniMobileScreen ? "text-[0.8rem] leading-[0.8rem]" : "text-[14px] leading-[15px]"
              } font-mono md:leading-5`}
              id="section3-main-text-row-4"
              style={{ opacity: 0 }}
            >
              Customize the design and add features that
            </p>
            <p
              className={`md:sticky fixed left-[5vw] bottom-[11vh] md:text-[16px] ${
                isMiniMobileScreen ? "text-[0.8rem] leading-[0.8rem]" : "text-[14px] leading-[15px]"
              } font-mono md:leading-5`}
              id="section3-main-text-row-5"
              style={{ opacity: 0 }}
            >
              cover your needs. Old school or new school.
            </p>
            <p
              className={`md:sticky fixed left-[5vw] bottom-[9vh] md:text-[16px] ${
                isMiniMobileScreen ? "text-[0.8rem] leading-[0.8rem]" : "text-[14px] leading-[15px]"
              } font-mono md:leading-5`}
              id="section3-main-text-row-6"
              style={{ opacity: 0 }}
            >
              We are where you are. Mobile, desktop,
            </p>
            <p
              className={`md:sticky fixed left-[5vw] bottom-[7vh] md:text-[16px] ${
                isMiniMobileScreen ? "text-[0.8rem] leading-[0.8rem]" : "text-[14px] leading-[15px]"
              } font-mono md:leading-5`}
              id="section3-main-text-row-7"
              style={{ opacity: 0 }}
            >
              digital and analogue.
            </p>
          </div>
        </div>
        <video
          autoPlay
          className="md:right-[8vw] md:top-[20vh] top-[11vh] md:h-[60vh] h-[53vh] z-10 opacity-0 fixed"
          controls={false}
          id="phone-frame"
          loop
          muted
          playsInline
          poster="/assets/images/landing/posters/phone-frame.png"
          preload="metadata"
          src="/assets/videos/phone-frame-optimised.mp4"
          style={{ transform: "scale(1.4)" }}
        />
      </div>
      {/* Section 4 */}
      {isDesktop ? (
        <div className="flex justify-around h-[200vh] z-10 relative" id={SECTIONS[3]}>
          <div
            className="flex flex-col gap-[8vh] justify-center w-[500px] leading-[33px] fixed top-[100vh] left-[15vw] cursor-default"
            id="section-4-text"
          >
            <p className="text-[30px] font-sans" id="section-4-text-row-1">
              {DEMOS[0].copy}
            </p>
            <p className="text-[30px] font-sans" id="section-4-text-row-2">
              {DEMOS[1].copy}
            </p>
            <p className="text-[30px] font-sans" id="section-4-text-row-3">
              {DEMOS[2].copy}
            </p>
          </div>
          <div
            className="flex flex-col justify-center items-center mr-[7vh] z-10 opacity-1 fixed top-[-100vh] right-[12vw]"
            id="section-4-preview"
          >
            <div className="h-[50vh] w-[50vh] rounded-[20px] border-[1px] border-black shadow-custom overflow-hidden">
              <video
                autoPlay
                className="h-full w-full object-cover"
                controls={false}
                loop
                muted
                playsInline
                poster={demo.poster}
                preload="metadata"
                src={demo.src}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex-col items-center gap-14 h-[235vh] py-[30vh] z-10 mt-[20vh] text-black relative flex"
          id={SECTIONS[3]}
        >
          {DEMOS.map((demo, key) => (
            <div className="flex flex-col items-center px-[5vw] gap-3" key={key}>
              <div className="w-[90vw] h-[90vw] rounded-[20px] border-[1px] border-black shadow-custom overflow-hidden">
                <video
                  autoPlay
                  className="h-full w-full object-cover"
                  controls={false}
                  loop
                  muted
                  playsInline
                  poster={demo.poster}
                  preload="metadata"
                  src={demo.src}
                />
              </div>
              <p className="text-[30px] font-sans leading-[30px]">{demo.copy}</p>
            </div>
          ))}
        </div>
      )}
      {/*Section 5*/}
      <div className="flex flex-col md:h-[300vh] relative md:mb-[30vh] mb-[50vh] cursor-default">
        <div
          className="flex flex-col mt-[30vh] mb-[20vh] items-center md:px-0 px-[4%] z-10"
          id={SECTIONS[4]}
        >
          <div
            className="md:text-[50px] flex flex-row gap-4 text-[30px] font-sans md:leading-[55px] leading-[30px] text-center opacity-1"
            id="section5-main-text-row1"
          >
            <p className="opacity-0" id="section5-main-text-row1-word1">
              Do
            </p>
            <p className="opacity-0" id="section5-main-text-row1-word2">
              you
            </p>
            <p className="opacity-0" id="section5-main-text-row1-word3">
              get
            </p>
            <p className="opacity-0" id="section5-main-text-row1-word4">
              it?
            </p>
          </div>
          <div
            className="md:text-[50px] flex flex-row gap-4 text-[30px] font-sans md:leading-[55px] leading-[30px] text-center opacity-1"
            id="section5-main-text-row2"
          >
            <p className="opacity-0" id="section5-main-text-row2-word1">
              WERZ
            </p>
            <p className="opacity-0" id="section5-main-text-row2-word2">
              is
            </p>
            <p className="opacity-0" id="section5-main-text-row2-word3">
              like
            </p>
            <p className="opacity-0" id="section5-main-text-row2-word4">
              an
            </p>
            {isDesktop && (
              <>
                <p className="opacity-0" id="section5-main-text-row2-word5">
                  operating
                </p>
                <p className="opacity-0" id="section5-main-text-row2-word6">
                  system.
                </p>
              </>
            )}
          </div>
          {!isDesktop && (
            <div
              className="md:text-[50px] flex flex-row gap-4 text-[30px] font-sans md:leading-[55px] leading-[30px] text-center opacity-1"
              id="section5-main-text-row2"
            >
              <p className="opacity-0" id="section5-main-text-row2-word5">
                operating
              </p>
              <p className="opacity-0" id="section5-main-text-row2-word6">
                system.
              </p>
            </div>
          )}
          <div
            className="md:text-[18px] md:mt-[-40px] mt-[-35px] text-[16px] font-mono md:w-[620px] text-center md:leading-[22px] leading-[16px] text-[#1B1D22]"
            id="section5-main-text-subtitle"
          >
            <p className="opacity-0" id="section5-main-text-row3">
              A partner understanding {!isDesktop && <br />} your needs, guiding your
            </p>
            <p className="opacity-0" id="section5-main-text-row4">
              growth, and shaping your {!isDesktop && <br />} vision. You set the direction,
            </p>
            <p className="opacity-0" id="section5-main-text-row5">
              and we ensure a smooth {!isDesktop && <br />} experience for you and your
            </p>
            <p className="opacity-0" id="section5-main-text-row6">
              members, making your community {!isDesktop && <br />} take the next step.
            </p>
          </div>
        </div>
        {isDesktop ? (
          <div className="flex justify-center gap-8 mt-[11vh] z-10" id="blocks">
            <div className="flex flex-col gap-6">
              {BLOCKS_INFO.slice(0, 4).map((block) => {
                const { description, id, src, title, badges } = block
                return (
                  <Block
                    badges={badges}
                    description={description}
                    id={id}
                    key={id}
                    src={src}
                    title={title}
                  />
                )
              })}
            </div>
            <div className="flex flex-col gap-6">
              {BLOCKS_INFO.slice(4, 8).map((block) => {
                const { description, id, src, title, badges } = block
                return (
                  <Block
                    badges={badges}
                    description={description}
                    id={id}
                    key={id}
                    src={src}
                    title={title}
                  />
                )
              })}
            </div>
            <div className="flex flex-col gap-6">
              {BLOCKS_INFO.slice(8, 12).map((block) => {
                const { description, id, src, title, badges } = block
                return (
                  <Block
                    badges={badges}
                    description={description}
                    id={id}
                    key={id}
                    src={src}
                    title={title}
                  />
                )
              })}
            </div>
            <div className="flex flex-col gap-6">
              {BLOCKS_INFO.slice(12, 16).map((block) => {
                const { description, id, src, title, badges } = block
                return (
                  <Block
                    badges={badges}
                    description={description}
                    id={id}
                    key={id}
                    src={src}
                    title={title}
                  />
                )
              })}
            </div>
          </div>
        ) : (
          <div className="flex justify-center gap-2 mt-[2vh] relative z-10">
            <div className="flex flex-col gap-6">
              {REORDERED_BLOCKS_INFO.slice(0, isLoadMore ? 16 : 4).map((block) => {
                const { description, id, src, title, badges } = block
                return (
                  <Block
                    badges={badges}
                    description={description}
                    id={id}
                    key={id}
                    src={src}
                    title={title}
                  />
                )
              })}
            </div>
            {!isLoadMore && (
              <div
                className="h-[350px] flex justify-center items-end absolute bottom-[-10px] left-0 right-0 z-20"
                style={{
                  background: "linear-gradient(to top, #E1E1E1, #E1E1E1 95px, transparent)",
                }}
              >
                <UbiqButton
                  buttonType={UbiqButtonType.LIGHT}
                  onClick={() => setIsLoadMore(true)}
                  size={UbiqButtonSize.SMALL}
                  styles="uppercase mb-6 font-mono"
                  title="More"
                />
              </div>
            )}
          </div>
        )}
      </div>
      {/*Section 6*/}
      <div
        className="flex flex-col justify-center items-center md:h-[130vh] h-[150vh] md:px-0 px-[6%] relative z-10 md:pt-[50vh] pt-[106vh]"
        id={SECTIONS[5]}
      >
        <Image
          alt=""
          className="md:w-[19vw] w-[55vw] absolute md:bottom-[7vh] bottom-[0vh] md:right-[12vw] right-[-18vw]"
          id="section-6-cloud"
          src={cloud1}
        />
        <Image
          alt=""
          className="md:w-[23vw] w-[57vw] absolute md:bottom-[-30vh] bottom-[-30vh] md:left-[-8vw] left-[-23vw]"
          id="section-6-cloud"
          src={cloud3}
        />
        <Image
          alt=""
          className="md:w-[25vw] w-[79vw] absolute z-20 md:bottom-[-80vh] bottom-[-80vh] md:left-[40vw] right-[-18vw]"
          id="section-6-cloud"
          src={cloud2}
        />
        {isDesktop ? (
          <div className="cursor-default md:text-[54px] text-[35px] flex flex-col items-center font-sans md:w-[830px] text-center md:leading-[50px] leading-[38px]">
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row1-word1">
                Join
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word2">
                the
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word3">
                growing
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word4">
                wave
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word5">
                of
              </p>
            </div>
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row2-word1">
                community
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word2">
                creators
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word3">
                and
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word4">
                unlock
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word5">
                your
              </p>
            </div>
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row3-word1">
                full
              </p>
              <p className="opacity-0" id="section6-main-text-row3-word2">
                potential
              </p>
            </div>
          </div>
        ) : (
          <div className="md:text-[54px] text-[35px] flex flex-col items-center font-sans md:w-[830px] text-center md:leading-[50px] leading-[38px] md:pb-0 pb-[30vh]">
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row1-word1">
                Join
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word2">
                the
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word3">
                growing
              </p>
            </div>
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row1-word4">
                wave
              </p>
              <p className="opacity-0" id="section6-main-text-row1-word5">
                of
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word1">
                community
              </p>
            </div>
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row2-word2">
                creators
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word3">
                and
              </p>
              <p className="opacity-0" id="section6-main-text-row2-word4">
                unlock
              </p>
            </div>
            <div className="flex flex-row gap-4">
              <p className="opacity-0" id="section6-main-text-row3-word1">
                your
              </p>
              <p className="opacity-0" id="section6-main-text-row3-word2">
                full
              </p>
              <p className="opacity-0" id="section6-main-text-row3-word3">
                potential
              </p>
            </div>
          </div>
        )}
        <div className="md:mt-[90vh] mt-[42vh] flex flex-col gap-4" id="section6-main-text2">
          <div className="md:text-[50px] flex justify-center gap-[2px] text-[35px] font-sans w-[830px] text-center leading-[50px] cursor-default">
            <p className="opacity-0" id="section6-main-text-symbol-1">
              D
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-2">
              o
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-3">
              n
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-4">
              '
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-5">
              t
            </p>
            <p className="ml-4 opacity-0" id="section6-main-text-symbol-6">
              s
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-7">
              n
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-8">
              o
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-9">
              o
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-10">
              z
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-11">
              e
            </p>
            <p className="opacity-0" id="section6-main-text-symbol-12">
              .
            </p>
          </div>
          <div className="opacity-0 md:mb-[45vh] mb-[47vh]" id="section6-button">
            <UbiqButton
              buttonType={UbiqButtonType.LIGHT}
              onClick={() => void sendEmail()}
              size={UbiqButtonSize.SMALL}
              styles="uppercase mt-[-32px] font-mono"
              title="JOIN WERZ"
            />
          </div>
        </div>
      </div>
      <div
        id="section-2-background"
        style={{
          background: isDesktop ? "#8dcbe0" : "#91c2d9",
          height: "120vh",
          left: 0,
          opacity: 0,
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 0,
        }}
      />
      <div
        id="section-4-background"
        style={{
          background: "#F4F4F4",
          height: "120vh",
          left: 0,
          opacity: 0,
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 0,
        }}
      />
      <div
        id="section-5-background"
        style={{
          background: "#E1E1E1",
          height: "120vh",
          left: 0,
          opacity: 0,
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 0,
        }}
      />
      <div
        id="section-6-background"
        style={{
          background: "#BFE0F6",
          height: "120vh",
          left: 0,
          opacity: 0,
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 0,
        }}
      />
      <video
        autoPlay
        className="fixed bottom-[-100px] md:top-[-12vh] top-[8vh] opacity-0 object-cover md:h-[170vh] w-full h-[calc(110vh+110px)]"
        controls={false}
        id="section-7-background"
        loop
        muted
        playsInline
        poster="/assets/images/landing/posters/pool.png"
        preload="metadata"
        src="/assets/videos/pool-optimised.mp4"
        style={{ opacity: 0, visibility: "hidden", willChange: "transform" }}
      />
    </div>
  )
}

export default Landing
